import { Button } from '@synoptic/ui-kit/button/button.tsx';
import { LockIcon } from '@synoptic/ui-kit/icons/react/lock.js';
import { MagnetEnergyIcon } from '@synoptic/ui-kit/icons/react/magnet-energy.tsx';
import { useIsTablet } from '@synoptic/ui-kit/responsive/hooks.ts';
import { useSearchParams } from 'react-router';
import {
  CustomTargetsDataFragmentFragment,
  PackageInStreamFragmentFragment,
  StreamPostCardFragmentFragment,
  StripePriceFragmentFragment,
} from '@/__generated__/graphql';
import { StreamSubscription } from '@/__generated__/schema-types.ts';
import { LayoutCard } from '@/components/layout/card';
import { PolymorphicPostCard } from '@/components/polymorphic-post-card';
import { TargetsModal } from '@/components/streams/custom-stream/targets-modal.tsx';
import { SubscribePaidStreamButton } from '@/components/streams/toggle-paid-stream-subscription/subscribe-paid-stream-button';
import { followStreamButton } from '@/routes/_app.p.streams_.$id/stream-banner/stream-banner.css.ts';
import { useFollowStream } from '@/routes/_app.p.streams_.$id/use-toggle-follow-stream.ts';
import { useMe } from '@/routes/_app/use-me.ts';
import { mockStreamPost } from './mock-stream-post';
import {
  lockedCustomStreamCtaPostAction,
  lockedStreamCtaPost,
  lockedStreamCtaPostAction,
} from './page.css';

export const LockedStreamCtaPost = ({
  streamId,
  inPackages,
  isPaid,
  prices,
  hasRights,
  showFollowButton,
}: {
  streamId: string;
  inPackages?: PackageInStreamFragmentFragment[];
  isPaid?: boolean;
  prices?: StripePriceFragmentFragment[];
  hasRights: boolean;
  showFollowButton?: boolean;
}) => {
  const [searchParams] = useSearchParams();

  const paymentSuccess = searchParams.get('success') === 'true';

  const label =
    isPaid && !hasRights
      ? 'Latest posts will be unlocked after you subscribe'
      : 'Posts will be unlocked after you follow';

  return (
    <div>
      <LayoutCard className={lockedStreamCtaPost}>
        <PolymorphicPostCard
          post={mockStreamPost as StreamPostCardFragmentFragment}
        />
        <div className={lockedStreamCtaPostAction}>
          <LockIcon width={40} height={40} />
          {label}
          {isPaid && !hasRights ? (
            <SubscribePaidStreamButton
              streamId={streamId}
              loading={paymentSuccess}
              packages={inPackages}
              prices={prices}
              popoverAlign="center"
            />
          ) : showFollowButton ? (
            <FollowStreamCtaButton streamId={streamId} />
          ) : null}
        </div>
      </LayoutCard>
    </div>
  );
};

const FollowStreamCtaButton = ({ streamId }: { streamId: string }) => {
  const isTablet = useIsTablet();
  const [followStream, { loading }] = useFollowStream(streamId);

  return (
    <Button
      onClick={followStream}
      variant={'primary'}
      size={isTablet ? 'medium' : 'small'}
      className={followStreamButton}
      loading={loading}
    >
      Follow
    </Button>
  );
};

export const LockedCustomStreamCtaPost = ({
  streamId,
  customTargetsData,
  accessAllowed,
  prices,
  authorUsername,
  purchasedTargetsNumber,
  subscription,
}: {
  streamId: string;
  customTargetsData: NonNullable<CustomTargetsDataFragmentFragment>;
  purchasedTargetsNumber: number;
  accessAllowed: boolean;
  authorUsername: string;
  prices?: StripePriceFragmentFragment[];
  subscription?: StreamSubscription | null;
}) => {
  const [searchParams] = useSearchParams();
  const me = useMe();

  const paymentSuccess = searchParams.get('success') === 'true';

  const isAuthor = me.username === authorUsername;

  const title = isAuthor
    ? `The buyers have not provided targets yet.`
    : accessAllowed
      ? 'You can request to monitor targets'
      : `You will be able to request to monitor targets after ${prices?.length ? 'purchase' : 'follow'}`;

  return (
    <div>
      <LayoutCard className={lockedStreamCtaPost}>
        <PolymorphicPostCard
          post={mockStreamPost as StreamPostCardFragmentFragment}
        />
        <div className={lockedCustomStreamCtaPostAction}>
          <MagnetEnergyIcon width={40} height={40} />
          {title}
          {isAuthor ? null : accessAllowed ? (
            <TargetsModal
              instruction={customTargetsData.targetTypeInstruction}
              maxTargets={purchasedTargetsNumber}
              trigger={<Button>Provide targets</Button>}
              targetValidationRegex={customTargetsData.targetValidationRegex}
              customTargetsData={customTargetsData}
              subscription={subscription}
            />
          ) : prices?.length ? (
            <SubscribePaidStreamButton
              streamId={streamId}
              loading={paymentSuccess}
              popoverAlign="center"
              customTargets={customTargetsData}
              prices={prices}
            />
          ) : null}
        </div>
      </LayoutCard>
    </div>
  );
};
